const REVIEW_LIST_API_URL = 'https://judge.me/api/v1/widgets/product_review';
const REVIEW_CREATE_API_URL = 'https://judge.me/api/v1/reviews';
const JUDGEME_PUBLIC_API_TOKEN = '_V8IcFg0bH0HZbspW0EzlS2dHkE';
const DEFAULT_REVIEW_RATING = 5;
const INITIAL_VISIBLE_REVIEW_COUNT = 2;
const LOAD_MORE_REVIEW_COUNT = 3;

async function fetchReviews({ shopDomain, apiToken, productId, page }) {
  const url = new URL(REVIEW_LIST_API_URL);
  url.searchParams.set('shop_domain', shopDomain);
  url.searchParams.set('api_token', apiToken);
  url.searchParams.set('external_id', productId);
  url.searchParams.set('page', String(page));
  url.searchParams.set('json_request', 'true');

  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Judge.me API returned ${response.status}`);

  return response.json();
}

async function submitReview({ shopDomain, productId, title, body, name, email }) {
  const payload = new URLSearchParams({
    shop_domain: shopDomain,
    platform: 'shopify',
    id: productId,
    title,
    body,
    name,
    email,
    reviewer_name_format: 'anonymous',
    rating: String(DEFAULT_REVIEW_RATING)
  });

  const response = await fetch(REVIEW_CREATE_API_URL, {
    method: 'POST',
    body: payload
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || 'レビューを送信できませんでした');
  }

  return data;
}

function textValue(value) {
  if (typeof value !== 'string') return '';

  const documentFragment = new DOMParser().parseFromString(value, 'text/html');
  documentFragment.querySelectorAll('br').forEach(element => element.replaceWith('\n'));
  return documentFragment.body.textContent?.trim() || '';
}

function normalizeReview(review) {
  return {
    title: textValue(review?.title || review?.review_title || ''),
    body: textValue(review?.body || review?.review_body || review?.content || '')
  };
}

function createReviewElement(review) {
  const article = document.createElement('article');
  article.className = 'lc-product-reviews__item';

  if (review.title) {
    const title = document.createElement('h3');
    title.className = 'lc-product-reviews__item-title lp-t-h4';
    title.textContent = review.title;
    article.append(title);
  }

  const body = document.createElement('p');
  body.className = 'lc-product-reviews__item-body lp-t-body-l';
  body.textContent = review.body;
  article.append(body);

  return article;
}

function initProductReviews(section) {
  if (section.dataset.initialized === 'true') return;
  section.dataset.initialized = 'true';

  const status = section.querySelector('[data-lc-review-status]');
  const empty = section.querySelector('[data-lc-review-empty]');
  const list = section.querySelector('[data-lc-review-list]');
  const loadMoreButton = section.querySelector('[data-lc-review-load-more]');
  const toggleFormButtons = section.querySelectorAll('[data-lc-review-toggle-form]');
  const action = section.querySelector('[data-lc-review-action]');
  const cancelButton = section.querySelector('[data-lc-review-cancel]');
  const formWrapper = section.querySelector('[data-lc-review-form-wrapper]');
  const form = section.querySelector('[data-lc-review-form]');
  const submitButton = section.querySelector('[data-lc-review-submit]');
  const submitStatus = section.querySelector('[data-lc-review-submit-status]');
  const {
    shopDomain,
    productId,
    errorText
  } = section.dataset;
  let currentPage = 0;
  let totalPages = 1;
  let pendingReviews = [];
  let isLoading = false;
  let isSubmitting = false;

  const updateReviewDisplay = () => {
    const hasReviews = list.childElementCount > 0;
    list.hidden = !hasReviews;
    empty.hidden = hasReviews;
    status.hidden = true;
    loadMoreButton.hidden = pendingReviews.length === 0 && currentPage >= totalPages;
  };

  const appendReviews = count => {
    pendingReviews.splice(0, count).forEach(review => {
      list.append(createReviewElement(review));
    });
    updateReviewDisplay();
  };

  const reloadReviews = async () => {
    list.replaceChildren();
    pendingReviews = [];
    currentPage = 0;
    totalPages = 1;
    await loadPage(1, INITIAL_VISIBLE_REVIEW_COUNT);
  };

  const loadPage = async (page, visibleCount) => {
    if (isLoading) return;

    isLoading = true;
    loadMoreButton.disabled = true;

    if (list.childElementCount === 0) {
      status.hidden = false;
    }

    try {
      const data = await fetchReviews({
        shopDomain,
        apiToken: JUDGEME_PUBLIC_API_TOKEN,
        productId,
        page
      });
      const reviews = Array.isArray(data.reviews)
        ? data.reviews.map(normalizeReview).filter(review => review.title || review.body)
        : [];

      pendingReviews.push(...reviews);
      currentPage = Number(data.current_page) || page;
      totalPages = Number(data.total_pages) || currentPage;
      appendReviews(visibleCount);
    } catch (error) {
      console.error(error);
      status.textContent = errorText;
      loadMoreButton.hidden = true;
    } finally {
      isLoading = false;
      loadMoreButton.disabled = false;
    }
  };

  const setFormExpanded = isExpanded => {
    toggleFormButtons.forEach(button => {
      button.setAttribute('aria-expanded', String(isExpanded));
    });
    formWrapper.hidden = !isExpanded;
    action.hidden = isExpanded;

    if (isExpanded) {
      form.querySelector('input[name="title"]')?.focus();
    }
  };

  toggleFormButtons.forEach(button => {
    button.addEventListener('click', () => setFormExpanded(true));
  });

  cancelButton?.addEventListener('click', () => setFormExpanded(false));

  form?.addEventListener('submit', async event => {
    event.preventDefault();

    if (isSubmitting) return;

    if (!form.reportValidity()) return;

    isSubmitting = true;
    form.setAttribute('aria-busy', 'true');
    submitButton.disabled = true;
    submitStatus.hidden = false;
    submitStatus.dataset.state = 'loading';
    submitStatus.textContent = '送信中です';

    const formData = new FormData(form);

    try {
      await submitReview({
        shopDomain,
        productId,
        title: String(formData.get('title') || '').trim(),
        body: String(formData.get('body') || '').trim(),
        name: String(formData.get('name') || '').trim(),
        email: String(formData.get('email') || '').trim()
      });

      form.reset();
      submitStatus.dataset.state = 'success';
      submitStatus.textContent = 'レビューを送信しました。反映までお時間をいただく場合があります。';
      await reloadReviews();
    } catch (error) {
      console.error(error);
      submitStatus.dataset.state = 'error';
      submitStatus.textContent = error.message || 'レビューを送信できませんでした。';
    } finally {
      isSubmitting = false;
      form.removeAttribute('aria-busy');
      submitButton.disabled = false;
    }
  });

  loadMoreButton?.addEventListener('click', () => {
    if (pendingReviews.length > 0) {
      appendReviews(LOAD_MORE_REVIEW_COUNT);
      return;
    }

    if (currentPage < totalPages) {
      loadPage(currentPage + 1, LOAD_MORE_REVIEW_COUNT);
    }
  });

  if (!shopDomain || !productId) {
    status.textContent = errorText;
    return;
  }

  reloadReviews();
}

document.querySelectorAll('[data-lc-product-reviews]').forEach(initProductReviews);

document.addEventListener('shopify:section:load', event => {
  event.target.querySelectorAll('[data-lc-product-reviews]').forEach(initProductReviews);
});
